import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ChartRenderer from '@/components/preview/ChartRenderer';
import ChartZoomViewport from '@/components/preview/ChartZoomViewport';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { DEFAULT_PRIMARY_COLOR, DEFAULT_SECONDARY_COLOR } from '@/constants';
import { isChartSpecEmpty, resolveChartSpec } from '@/lib/chart-config';
import {
    buildTableChartMultiDatapoints,
    explainEmptyChart,
    extractTableChartData,
    getColumnLabelById,
    getSafeTableChartSelection,
    toNormalizedTableView,
} from '@/lib/table-chart';
import { cn } from '@/lib/utils';
import DataAndTypeStep from './chart-config/DataAndTypeStep';
import StyleStep from './chart-config/StyleStep';
import { toNumber, type BandRow, type CutoffRow } from './chart-config/types';
import { METRIC_CHART_DEFAULTS } from './chart-types/chart-metric-defaults';
import {
    getChartTypeDefinition,
    type AxisEndLabelsEditorValue,
    type ChartTypeId,
} from './chart-types/registry';
import type {
    ChartAxisConfig,
    ChartBlock,
    ChartCutoffLine,
    ChartDatapoints,
    ChartReferenceBand,
    ChartType,
    TableBlock,
} from '@/types/report-payload';

interface ChartConfigModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    availableTables: TableBlock[];
    selectedTableId?: string | null;
    lockTableSelection?: boolean;
    isEditing?: boolean;
    /** Report brand colors used as the default series colors (preview + swatches). */
    primaryColor?: string;
    secondaryColor?: string;
    initialConfig?: {
        chartType?: ChartType;
        linkedTableId?: string;
        xAxisColumnId?: string;
        yAxisColumnIds?: string[];
        measurementType?: string;
        title?: string;
        referenceBands?: ChartReferenceBand[];
        cutoffLines?: ChartCutoffLine[];
        axis?: ChartAxisConfig;
        quadrant?: { x?: number; y?: number };
        axisEndLabels?: ChartBlock['axis_end_labels'];
        seriesColors?: Record<string, string>;
        seriesSymbols?: Record<string, string>;
        groupColumnId?: string;
        colorByGroup?: boolean;
        colorByClassification?: boolean;
        highlight?: ChartBlock['highlight'];
        showDeltas?: boolean;
        lineStyle?: ChartBlock['line_style'];
        barStyle?: ChartBlock['bar_style'];
        pointLabels?: boolean;
        referenceColumnIds?: string[];
        bandColor?: string;
        excludedXLabels?: string[];
        pointColors?: Record<string, string>;
    };
    onConfirm: (config: {
        chartType: ChartType;
        linkedTableId: string;
        xAxisColumnId: string;
        yAxisColumnIds: string[];
        measurementType: string;
        title?: string;
        referenceBands?: ChartReferenceBand[];
        cutoffLines?: ChartCutoffLine[];
        axis?: ChartAxisConfig;
        quadrant?: { x?: number; y?: number };
        axisEndLabels?: ChartBlock['axis_end_labels'];
        seriesColors?: Record<string, string>;
        seriesSymbols?: Record<string, string>;
        groupColumnId?: string;
        colorByGroup?: boolean;
        colorByClassification?: boolean;
        highlight?: ChartBlock['highlight'];
        showDeltas?: boolean;
        lineStyle?: ChartBlock['line_style'];
        barStyle?: ChartBlock['bar_style'];
        pointLabels?: boolean;
        referenceColumnIds?: string[];
        bandColor?: string;
        excludedXLabels?: string[];
        pointColors?: Record<string, string>;
        datapoints: ChartDatapoints;
    }) => void;
}

// Chart types whose marks carry per-point score labels (clinical convention).
const POINT_LABEL_TYPES: ReadonlySet<string> = new Set(['bar', 'line', 'scatter']);

// Chart types whose marks carry a per-series marker shape.
const SYMBOL_TYPES: ReadonlySet<string> = new Set(['line', 'scatter']);

// Chart types whose extractors honor `excluded_x_labels` (the hide-rows list).
const ROW_FILTER_TYPES: ReadonlySet<string> = new Set([
    'bar',
    'line',
    'radar',
    'scatter',
    'distribution',
    'level_bars',
]);

const EMPTY_AXIS_END_LABELS: AxisEndLabelsEditorValue = {
    x_min: '',
    x_max: '',
    y_min: '',
    y_max: '',
};

// Numeric columns whose (de-accented) label reads as norm/population context
// are auto-added as a reference series on creation.
const NORM_LABEL_RE = /populac|norma|controle|grupo|referencia/;

const STEPS = ['Dados & tipo', 'Estilo & ajustes'] as const;

export default function ChartConfigModal({
    open,
    onOpenChange,
    availableTables,
    selectedTableId,
    lockTableSelection = false,
    isEditing = false,
    primaryColor = DEFAULT_PRIMARY_COLOR,
    secondaryColor = DEFAULT_SECONDARY_COLOR,
    initialConfig,
    onConfirm,
}: ChartConfigModalProps) {
    const [step, setStep] = useState(0);
    const [chartType, setChartType] = useState<ChartType>('bar');
    const [currentTableId, setCurrentTableId] = useState('');
    const [xAxisColumnId, setXAxisColumnId] = useState('');
    const [yAxisColumnIds, setYAxisColumnIds] = useState<string[]>([]);
    const [title, setTitle] = useState('');
    const [referenceBands, setReferenceBands] = useState<BandRow[]>([]);
    const [cutoffLines, setCutoffLines] = useState<CutoffRow[]>([]);
    const [yAxisMin, setYAxisMin] = useState('');
    const [yAxisMax, setYAxisMax] = useState('');
    const [quadrantX, setQuadrantX] = useState('');
    const [quadrantY, setQuadrantY] = useState('');
    const [axisEndLabels, setAxisEndLabels] =
        useState<AxisEndLabelsEditorValue>(EMPTY_AXIS_END_LABELS);
    const [seriesColors, setSeriesColors] = useState<Record<string, string>>({});
    const [seriesSymbols, setSeriesSymbols] = useState<Record<string, string>>({});
    const [groupColumnId, setGroupColumnId] = useState('');
    const [colorByGroup, setColorByGroup] = useState(false);
    const [colorByClassification, setColorByClassification] = useState(false);
    const [pointColors, setPointColors] = useState<Record<string, string>>({});
    const [highlightLabel, setHighlightLabel] = useState('');
    const [highlightColor, setHighlightColor] = useState('');
    const [showDeltas, setShowDeltas] = useState(false);
    const [lineCurve, setLineCurve] = useState<'straight' | 'smooth'>('smooth');
    const [fillArea, setFillArea] = useState(false);
    const [fillGradient, setFillGradient] = useState(true);
    const [barCorner, setBarCorner] = useState<'square' | 'soft' | 'round'>('soft');
    const [barThickness, setBarThickness] = useState<'thin' | 'normal' | 'thick'>('normal');
    const [barGradient, setBarGradient] = useState(true);
    const [excludedXLabels, setExcludedXLabels] = useState<string[]>([]);
    const [pointLabels, setPointLabels] = useState(true);
    const [referenceColumnIds, setReferenceColumnIds] = useState<string[]>([]);
    // Distribution "background" base color; the 5 classification tones derive
    // from it. Empty → the neutral gray default.
    const [bandColor, setBandColor] = useState('');
    // Type card under the pointer: the preview shows THAT type (with the user's
    // data) without applying anything. The active card is the current chartType.
    const [hoveredType, setHoveredType] = useState<ChartTypeId | null>(null);
    // One-shot guard for the creation-time auto-configure below.
    const autoConfigured = useRef(false);

    useEffect(() => {
        if (!open) return;

        setStep(0);
        setChartType(initialConfig?.chartType || 'bar');
        setCurrentTableId(selectedTableId || initialConfig?.linkedTableId || '');
        setTitle(initialConfig?.title || '');
        setQuadrantX(
            initialConfig?.quadrant?.x !== undefined ? String(initialConfig.quadrant.x) : '',
        );
        setQuadrantY(
            initialConfig?.quadrant?.y !== undefined ? String(initialConfig.quadrant.y) : '',
        );
        setAxisEndLabels({
            x_min: initialConfig?.axisEndLabels?.x_min ?? '',
            x_max: initialConfig?.axisEndLabels?.x_max ?? '',
            y_min: initialConfig?.axisEndLabels?.y_min ?? '',
            y_max: initialConfig?.axisEndLabels?.y_max ?? '',
        });
        setReferenceBands(
            (initialConfig?.referenceBands ?? []).map((band) => ({
                label: band.label ?? '',
                min: String(band.min),
                max: String(band.max),
                color: band.color ?? '',
            })),
        );
        setCutoffLines(
            (initialConfig?.cutoffLines ?? []).map((cutoff) => ({
                label: cutoff.label ?? '',
                value: String(cutoff.value),
                color: cutoff.color ?? '',
            })),
        );
        setYAxisMin(initialConfig?.axis?.min !== undefined ? String(initialConfig.axis.min) : '');
        setYAxisMax(initialConfig?.axis?.max !== undefined ? String(initialConfig.axis.max) : '');
        setSeriesColors(initialConfig?.seriesColors ?? {});
        setSeriesSymbols(initialConfig?.seriesSymbols ?? {});
        setGroupColumnId(initialConfig?.groupColumnId ?? '');
        setColorByGroup(initialConfig?.colorByGroup === true);
        setColorByClassification(initialConfig?.colorByClassification === true);
        setPointColors(initialConfig?.pointColors ?? {});
        setHighlightLabel(initialConfig?.highlight?.x_label ?? '');
        setHighlightColor(initialConfig?.highlight?.color ?? '');
        setShowDeltas(initialConfig?.showDeltas === true);
        setBandColor(initialConfig?.bandColor ?? '');
        setLineCurve(initialConfig?.lineStyle?.curve ?? 'smooth');
        setFillArea(initialConfig?.lineStyle?.fill ?? false);
        setFillGradient(initialConfig?.lineStyle?.fill_gradient !== false);
        setBarCorner(initialConfig?.barStyle?.corner ?? 'soft');
        setBarThickness(initialConfig?.barStyle?.thickness ?? 'normal');
        setBarGradient(initialConfig?.barStyle?.gradient !== false);
        setExcludedXLabels(initialConfig?.excludedXLabels ?? []);
        setPointLabels(initialConfig?.pointLabels !== false);
        setReferenceColumnIds(initialConfig?.referenceColumnIds ?? []);
        // The gallery highlights the current type directly via `chartType`, so
        // no extra "active" state to seed here.
        setHoveredType(null);
        autoConfigured.current = false;
    }, [
        isEditing,
        initialConfig?.chartType,
        initialConfig?.linkedTableId,
        initialConfig?.title,
        initialConfig?.referenceBands,
        initialConfig?.cutoffLines,
        initialConfig?.axis,
        initialConfig?.quadrant,
        initialConfig?.axisEndLabels,
        initialConfig?.seriesColors,
        initialConfig?.seriesSymbols,
        initialConfig?.groupColumnId,
        initialConfig?.colorByGroup,
        initialConfig?.colorByClassification,
        initialConfig?.highlight,
        initialConfig?.pointColors,
        initialConfig?.showDeltas,
        initialConfig?.lineStyle,
        initialConfig?.barStyle,
        initialConfig?.pointLabels,
        initialConfig?.referenceColumnIds,
        initialConfig?.bandColor,
        initialConfig?.excludedXLabels,
        open,
        selectedTableId,
    ]);

    const currentTable = useMemo(() => {
        return availableTables.find((table) => table.id === currentTableId) || null;
    }, [availableTables, currentTableId]);

    const tableChartData = useMemo(() => {
        return extractTableChartData(currentTable);
    }, [currentTable]);

    const suggestedSelection = useMemo(() => {
        return getSafeTableChartSelection(
            tableChartData,
            currentTableId === initialConfig?.linkedTableId
                ? initialConfig?.xAxisColumnId
                : undefined,
            currentTableId === initialConfig?.linkedTableId
                ? initialConfig?.yAxisColumnIds?.[0]
                : undefined,
            initialConfig?.measurementType,
        );
    }, [
        currentTableId,
        initialConfig?.linkedTableId,
        initialConfig?.measurementType,
        initialConfig?.xAxisColumnId,
        initialConfig?.yAxisColumnIds,
        tableChartData,
    ]);

    useEffect(() => {
        if (!open) return;

        setXAxisColumnId(suggestedSelection.xAxisColumnId || '');
        const editingThisTable =
            currentTableId === initialConfig?.linkedTableId &&
            !!initialConfig?.yAxisColumnIds?.length;
        setYAxisColumnIds(
            editingThisTable
                ? (initialConfig?.yAxisColumnIds as string[])
                : suggestedSelection.yAxisColumnId
                  ? [suggestedSelection.yAxisColumnId]
                  : [],
        );
    }, [
        open,
        suggestedSelection.xAxisColumnId,
        suggestedSelection.yAxisColumnId,
        currentTableId,
        initialConfig?.linkedTableId,
        initialConfig?.yAxisColumnIds,
    ]);

    // Series follow COLUMN order (stable), regardless of checkbox click order.
    const orderedYAxisColumnIds = useMemo(
        () =>
            tableChartData.columns
                .map((column) => column.id)
                .filter((id) => yAxisColumnIds.includes(id)),
        [tableChartData.columns, yAxisColumnIds],
    );

    // Per-type behavior comes from the UI registry, not inline switches.
    const typeDef = getChartTypeDefinition(chartType);

    const toggleYAxisColumn = (columnId: string) => {
        // Single-series geometries (e.g. xy-scatter) pick exactly one Y column.
        if (!typeDef.supports.multiSeries) {
            setYAxisColumnIds((prev) => (prev.includes(columnId) ? [] : [columnId]));
            return;
        }
        setYAxisColumnIds((prev) =>
            prev.includes(columnId) ? prev.filter((id) => id !== columnId) : [...prev, columnId],
        );
    };

    // Decorations are cartesian-only; radar has no y scale to hang them off.
    const showDecorations = typeDef.supports.decorations;

    const addBand = () =>
        setReferenceBands((prev) => [...prev, { label: '', min: '', max: '', color: '' }]);
    const updateBand = (index: number, patch: Partial<BandRow>) =>
        setReferenceBands((prev) =>
            prev.map((band, i) => (i === index ? { ...band, ...patch } : band)),
        );
    const removeBand = (index: number) =>
        setReferenceBands((prev) => prev.filter((_, i) => i !== index));

    const addCutoff = () =>
        setCutoffLines((prev) => [...prev, { label: '', value: '', color: '' }]);
    const updateCutoff = (index: number, patch: Partial<CutoffRow>) =>
        setCutoffLines((prev) =>
            prev.map((cutoff, i) => (i === index ? { ...cutoff, ...patch } : cutoff)),
        );
    const removeCutoff = (index: number) =>
        setCutoffLines((prev) => prev.filter((_, i) => i !== index));

    // Parse editor rows into the persisted shapes, dropping incomplete rows.
    // Radar drops all decorations so a switched chart never carries stale ones.
    const parsedBands = useMemo<ChartReferenceBand[]>(() => {
        if (!showDecorations) return [];
        return referenceBands
            .map((band) => ({
                min: toNumber(band.min),
                max: toNumber(band.max),
                ...(band.label.trim() ? { label: band.label.trim() } : {}),
                ...(band.color.trim() ? { color: band.color.trim() } : {}),
            }))
            .filter((band) => Number.isFinite(band.min) && Number.isFinite(band.max));
    }, [referenceBands, showDecorations]);

    const parsedCutoffs = useMemo<ChartCutoffLine[]>(() => {
        if (!showDecorations) return [];
        return cutoffLines
            .map((cutoff) => ({
                value: toNumber(cutoff.value),
                ...(cutoff.label.trim() ? { label: cutoff.label.trim() } : {}),
                ...(cutoff.color.trim() ? { color: cutoff.color.trim() } : {}),
            }))
            .filter((cutoff) => Number.isFinite(cutoff.value));
    }, [cutoffLines, showDecorations]);

    const parsedAxis = useMemo<ChartAxisConfig | undefined>(() => {
        if (!showDecorations) return undefined;
        const min = toNumber(yAxisMin);
        const max = toNumber(yAxisMax);
        const axis: ChartAxisConfig = {
            ...(Number.isFinite(min) ? { min } : {}),
            ...(Number.isFinite(max) ? { max } : {}),
        };
        return axis.min !== undefined || axis.max !== undefined ? axis : undefined;
    }, [yAxisMin, yAxisMax, showDecorations]);

    // Quadrant dividers only apply to the xy-scatter geometry.
    const parsedQuadrant = useMemo<{ x?: number; y?: number } | undefined>(() => {
        if (chartType !== 'xy_scatter') return undefined;
        const x = toNumber(quadrantX);
        const y = toNumber(quadrantY);
        const quadrant: { x?: number; y?: number } = {
            ...(Number.isFinite(x) ? { x } : {}),
            ...(Number.isFinite(y) ? { y } : {}),
        };
        return quadrant.x !== undefined || quadrant.y !== undefined ? quadrant : undefined;
    }, [chartType, quadrantX, quadrantY]);

    // Axis-extreme labels only apply to the xy-scatter geometry; empty inputs drop the key.
    const parsedAxisEndLabels = useMemo<ChartBlock['axis_end_labels']>(() => {
        if (chartType !== 'xy_scatter') return undefined;
        const labels = {
            ...(axisEndLabels.x_min.trim() ? { x_min: axisEndLabels.x_min.trim() } : {}),
            ...(axisEndLabels.x_max.trim() ? { x_max: axisEndLabels.x_max.trim() } : {}),
            ...(axisEndLabels.y_min.trim() ? { y_min: axisEndLabels.y_min.trim() } : {}),
            ...(axisEndLabels.y_max.trim() ? { y_max: axisEndLabels.y_max.trim() } : {}),
        };
        return Object.keys(labels).length > 0 ? labels : undefined;
    }, [chartType, axisEndLabels]);

    const previewDatapoints = useMemo(() => {
        if (!currentTable || !xAxisColumnId || orderedYAxisColumnIds.length === 0) {
            return { x_axis: [], y_axis: [], series: [] } as ChartDatapoints;
        }

        return buildTableChartMultiDatapoints(currentTable, {
            x_axis_column_id: xAxisColumnId,
            y_axis_column_ids: orderedYAxisColumnIds,
            skip_empty: true,
        });
    }, [currentTable, xAxisColumnId, orderedYAxisColumnIds]);

    // Legacy single-series label (first y column) — also the measurement_type.
    const firstYAxisLabel = getColumnLabelById(tableChartData, orderedYAxisColumnIds[0]);

    // Only persist colors for columns that are actually selected.
    const selectedSeriesColors = useMemo<Record<string, string>>(() => {
        return Object.fromEntries(
            orderedYAxisColumnIds
                .filter((id) => seriesColors[id])
                .map((id) => [id, seriesColors[id]]),
        );
    }, [orderedYAxisColumnIds, seriesColors]);

    // Same rule for marker shapes: only the selected columns' picks survive.
    const selectedSeriesSymbols = useMemo<Record<string, string>>(() => {
        return Object.fromEntries(
            orderedYAxisColumnIds
                .filter((id) => seriesSymbols[id])
                .map((id) => [id, seriesSymbols[id]]),
        );
    }, [orderedYAxisColumnIds, seriesSymbols]);

    // Grouping is registry-gated; a pick that collides with the current x/y
    // columns (or no longer exists) is dropped rather than sent to the renderer.
    const groupingSupported = !!typeDef.supports.grouping;
    const selectedGroupColumnId = useMemo(() => {
        if (!groupingSupported || !groupColumnId) return undefined;
        if (groupColumnId === xAxisColumnId) return undefined;
        if (orderedYAxisColumnIds.includes(groupColumnId)) return undefined;
        if (!tableChartData.columns.some((column) => column.id === groupColumnId)) return undefined;
        return groupColumnId;
    }, [
        groupingSupported,
        groupColumnId,
        xAxisColumnId,
        orderedYAxisColumnIds,
        tableChartData.columns,
    ]);

    // Per-group bar color families only make sense for a single-series bar
    // whose categories are actually grouped.
    const colorByGroupSupported =
        chartType === 'bar' && !!selectedGroupColumnId && orderedYAxisColumnIds.length === 1;

    // Per-category emphasis (manual colors, classification tint, highlight)
    // applies to single-series marks only.
    const pointEmphasisSupported =
        POINT_LABEL_TYPES.has(chartType) && orderedYAxisColumnIds.length === 1;

    // Categories the emphasis controls can reference (hidden rows excluded).
    const visibleXLabels = useMemo(
        () => previewDatapoints.x_axis.filter((label) => !excludedXLabels.includes(label)),
        [previewDatapoints.x_axis, excludedXLabels],
    );

    // Only persist per-category picks for categories that still exist.
    const selectedPointColors = useMemo<Record<string, string>>(
        () =>
            Object.fromEntries(
                Object.entries(pointColors).filter(
                    ([label, color]) => color && visibleXLabels.includes(label),
                ),
            ),
        [pointColors, visibleXLabels],
    );

    const selectedHighlight = useMemo(
        () =>
            highlightLabel && visibleXLabels.includes(highlightLabel)
                ? {
                      x_label: highlightLabel,
                      ...(highlightColor ? { color: highlightColor } : {}),
                  }
                : undefined,
        [highlightLabel, highlightColor, visibleXLabels],
    );

    // Evolution labels need exactly two DATA series (references don't count).
    const dataYAxisColumnIds = useMemo(
        () => orderedYAxisColumnIds.filter((id) => !referenceColumnIds.includes(id)),
        [orderedYAxisColumnIds, referenceColumnIds],
    );
    const deltasSupported =
        (chartType === 'bar' || chartType === 'line') && dataYAxisColumnIds.length === 2;

    // Marker shapes apply to the mark-based line/scatter types only.
    const symbolsSupported = SYMBOL_TYPES.has(chartType);

    // Line appearance only applies to the line geometry; the fill fade toggle
    // is stored only when the fill is on and the fade was turned off.
    const lineStyle =
        chartType === 'line'
            ? {
                  curve: lineCurve,
                  fill: fillArea,
                  ...(fillArea && !fillGradient ? { fill_gradient: false } : {}),
              }
            : undefined;

    // Bar appearance deviations from the default look (soft corners, normal
    // thickness, gradient fill); undefined when everything is default.
    const rawBarStyle = useMemo(() => {
        const fragment = {
            ...(barCorner !== 'soft' ? { corner: barCorner } : {}),
            ...(barThickness !== 'normal' ? { thickness: barThickness } : {}),
            ...(barGradient === false ? { gradient: false } : {}),
        };
        return Object.keys(fragment).length ? fragment : undefined;
    }, [barCorner, barThickness, barGradient]);
    const barStyle = chartType === 'bar' ? rawBarStyle : undefined;

    // Per-point score labels apply to the mark-based cartesian types only.
    const pointLabelsSupported = POINT_LABEL_TYPES.has(chartType);

    // Norm/reference flags only make sense for the currently selected columns.
    const selectedReferenceIds = useMemo(
        () => referenceColumnIds.filter((id) => orderedYAxisColumnIds.includes(id)),
        [referenceColumnIds, orderedYAxisColumnIds],
    );

    // Picking a gallery card sets the TYPE only — title, scale, bands and
    // cutoffs are never prefilled here, and any hand-entered ones survive the
    // switch. (The conventional scale is auto-filled once on open, below.)
    const applyChartType = (nextType: ChartTypeId) => {
        setChartType(nextType);
    };

    // Instant chart on open (creation only — the edit flow must respect the
    // stored config): the metric detected on the suggested Y column picks the
    // conventional chart type + scale, and norm/population columns are auto-marked
    // as reference series — the modal opens showing a finished chart the user
    // tweaks instead of builds. One-shot per open; hand-picking always wins after.
    useEffect(() => {
        if (!open || isEditing || autoConfigured.current) return;
        if (!currentTable) return;
        const suggestedYId = suggestedSelection.yAxisColumnId;
        if (!suggestedYId) return;
        autoConfigured.current = true;

        const suggestedY = tableChartData.columns.find((column) => column.id === suggestedYId);
        const metricDefault = suggestedY?.metricId
            ? METRIC_CHART_DEFAULTS[suggestedY.metricId]
            : undefined;
        if (metricDefault) {
            setChartType(metricDefault.chartType);
            if (metricDefault.axis) {
                setYAxisMin(
                    metricDefault.axis.min !== undefined ? String(metricDefault.axis.min) : '',
                );
                setYAxisMax(
                    metricDefault.axis.max !== undefined ? String(metricDefault.axis.max) : '',
                );
            }
            if (metricDefault.lineStyle) {
                setLineCurve(metricDefault.lineStyle.curve);
                setFillArea(metricDefault.lineStyle.fill);
            }
        }

        // Norm columns only make sense on multi-series types.
        if (!getChartTypeDefinition(metricDefault?.chartType ?? 'bar').supports.multiSeries) return;
        const normIds = tableChartData.columns
            .filter(
                (column) =>
                    column.id !== suggestedYId &&
                    column.id !== suggestedSelection.xAxisColumnId &&
                    column.isNumericCandidate &&
                    NORM_LABEL_RE.test(column.normalizedLabel),
            )
            .map((column) => column.id);
        if (normIds.length === 0) return;
        setYAxisColumnIds((prev) => [...prev, ...normIds.filter((id) => !prev.includes(id))]);
        setReferenceColumnIds(normIds);
    });

    // Preview goes through the SAME resolveChartSpec the saved chart + .docx use,
    // so the modal is literally WYSIWYG. The builder is shared by the committed
    // state's preview and the hover-a-model preview (same data, candidate styling).
    const buildPreviewSpec = useCallback(
        (candidate: {
            chartType: ChartType;
            bands: ChartReferenceBand[];
            cutoffs: ChartCutoffLine[];
            axis?: ChartAxisConfig;
            quadrant?: { x?: number; y?: number };
            axisEndLabels?: ChartBlock['axis_end_labels'];
            lineStyle?: ChartBlock['line_style'];
            barStyle?: ChartBlock['bar_style'];
            pointLabels?: boolean;
            colorByGroup?: boolean;
        }) => {
            if (!currentTable || !xAxisColumnId || orderedYAxisColumnIds.length === 0) return null;
            // Per-category emphasis only renders on single-series marks.
            const emphasisOk =
                POINT_LABEL_TYPES.has(candidate.chartType) && orderedYAxisColumnIds.length === 1;
            const deltasOk =
                (candidate.chartType === 'bar' || candidate.chartType === 'line') &&
                dataYAxisColumnIds.length === 2;
            return resolveChartSpec(
                {
                    chart_type: candidate.chartType,
                    source_mode: 'linked_table',
                    title,
                    measurement_type: firstYAxisLabel,
                    source: {
                        x_axis_column_id: xAxisColumnId,
                        y_axis_column_id: orderedYAxisColumnIds[0],
                        y_axis_column_ids: orderedYAxisColumnIds,
                        skip_empty: true,
                        ...(Object.keys(selectedSeriesColors).length
                            ? { series_colors: selectedSeriesColors }
                            : {}),
                        ...(selectedReferenceIds.length
                            ? { reference_column_ids: selectedReferenceIds }
                            : {}),
                        ...(bandColor ? { band_color: bandColor } : {}),
                        ...(Object.keys(selectedSeriesSymbols).length
                            ? { series_symbols: selectedSeriesSymbols }
                            : {}),
                        ...(selectedGroupColumnId
                            ? { group_column_id: selectedGroupColumnId }
                            : {}),
                        ...(excludedXLabels.length ? { excluded_x_labels: excludedXLabels } : {}),
                        ...(emphasisOk && Object.keys(selectedPointColors).length
                            ? { point_colors: selectedPointColors }
                            : {}),
                    },
                    ...(candidate.bands.length ? { reference_bands: candidate.bands } : {}),
                    ...(candidate.cutoffs.length ? { cutoff_lines: candidate.cutoffs } : {}),
                    ...(candidate.axis ? { axis: candidate.axis } : {}),
                    ...(candidate.quadrant ? { quadrant: candidate.quadrant } : {}),
                    ...(candidate.axisEndLabels
                        ? { axis_end_labels: candidate.axisEndLabels }
                        : {}),
                    ...(candidate.lineStyle ? { line_style: candidate.lineStyle } : {}),
                    ...(candidate.barStyle ? { bar_style: candidate.barStyle } : {}),
                    ...(candidate.pointLabels === false ? { point_labels: false } : {}),
                    ...(candidate.colorByGroup ? { color_by_group: true } : {}),
                    ...(emphasisOk && colorByClassification && candidate.bands.length
                        ? { color_by_classification: true }
                        : {}),
                    ...(emphasisOk && selectedHighlight ? { highlight: selectedHighlight } : {}),
                    ...(deltasOk && showDeltas ? { show_deltas: true } : {}),
                },
                // Brand colors seed the default series colors, so the modal
                // preview matches the report editor and the .docx raster exactly.
                { table: toNormalizedTableView(currentTable), primaryColor, secondaryColor },
            );
        },
        [
            currentTable,
            xAxisColumnId,
            orderedYAxisColumnIds,
            title,
            firstYAxisLabel,
            selectedSeriesColors,
            selectedSeriesSymbols,
            selectedGroupColumnId,
            selectedReferenceIds,
            bandColor,
            excludedXLabels,
            selectedPointColors,
            colorByClassification,
            selectedHighlight,
            dataYAxisColumnIds,
            showDeltas,
            primaryColor,
            secondaryColor,
        ],
    );

    const previewSpec = useMemo(
        () =>
            buildPreviewSpec({
                chartType,
                bands: parsedBands,
                cutoffs: parsedCutoffs,
                axis: parsedAxis,
                quadrant: parsedQuadrant,
                axisEndLabels: parsedAxisEndLabels,
                lineStyle,
                barStyle,
                pointLabels: pointLabelsSupported ? pointLabels : undefined,
                colorByGroup: colorByGroupSupported ? colorByGroup : undefined,
            }),
        [
            buildPreviewSpec,
            chartType,
            parsedBands,
            parsedCutoffs,
            parsedAxis,
            parsedQuadrant,
            parsedAxisEndLabels,
            lineStyle,
            barStyle,
            pointLabelsSupported,
            pointLabels,
            colorByGroupSupported,
            colorByGroup,
        ],
    );

    // Hovering a card: the user's data drawn with the CANDIDATE type, using the
    // current scale/style — nothing is applied until the click (which only
    // switches the type). Hand-entered bands/cutoffs are kept.
    const hoveredSpec = useMemo(() => {
        if (!hoveredType) return null;
        return buildPreviewSpec({
            chartType: hoveredType,
            bands: parsedBands,
            cutoffs: parsedCutoffs,
            axis: parsedAxis,
            quadrant: parsedQuadrant,
            axisEndLabels: parsedAxisEndLabels,
            lineStyle:
                hoveredType === 'line'
                    ? {
                          curve: lineCurve,
                          fill: fillArea,
                          ...(fillArea && !fillGradient ? { fill_gradient: false } : {}),
                      }
                    : undefined,
            barStyle: hoveredType === 'bar' ? rawBarStyle : undefined,
            pointLabels: POINT_LABEL_TYPES.has(hoveredType) ? pointLabels : undefined,
            colorByGroup: hoveredType === 'bar' ? colorByGroup : undefined,
        });
    }, [
        hoveredType,
        buildPreviewSpec,
        parsedBands,
        parsedCutoffs,
        parsedAxis,
        parsedQuadrant,
        parsedAxisEndLabels,
        rawBarStyle,
        fillGradient,
        lineCurve,
        fillArea,
        pointLabels,
        colorByGroup,
    ]);

    // The gallery only exists on step 1; drop a lingering hover when leaving it.
    useEffect(() => {
        if (step !== 0) setHoveredType(null);
    }, [step]);

    // Rows dropped from the chart because their x label was empty.
    const skippedRowCount = useMemo(() => {
        if (!currentTable || !xAxisColumnId || orderedYAxisColumnIds.length === 0) return 0;
        return Math.max(0, tableChartData.totalRows - previewDatapoints.x_axis.length);
    }, [
        currentTable,
        previewDatapoints.x_axis.length,
        tableChartData.totalRows,
        xAxisColumnId,
        orderedYAxisColumnIds.length,
    ]);

    const hasTable = !!currentTable;
    // A spec can resolve empty even with datapoints present (e.g. a gauge over
    // a text-only column), so confirmation requires a PLOTTABLE spec — the
    // same rule the preview and the report placeholder use.
    const canConfirm =
        hasTable &&
        !!xAxisColumnId &&
        orderedYAxisColumnIds.length > 0 &&
        !!previewSpec &&
        !isChartSpecEmpty(previewSpec);

    const emptyMessage =
        availableTables.length === 0
            ? 'Adicione pelo menos uma tabela antes de criar um gráfico.'
            : !currentTable
              ? 'Selecione a tabela de origem para configurar o gráfico.'
              : // Columns picked but nothing plotted → say WHY (no rows, no
                // numeric values, no label/value pairing).
                (explainEmptyChart(currentTable, {
                    x_axis_column_id: xAxisColumnId,
                    y_axis_column_ids: orderedYAxisColumnIds,
                }) ??
                'Escolha uma coluna descritiva no eixo X e uma ou mais colunas numéricas no eixo Y.');

    const confirmLabel = isEditing ? 'Salvar alterações' : 'Adicionar gráfico';

    const handleConfirm = () => {
        if (!currentTable || !xAxisColumnId || orderedYAxisColumnIds.length === 0) return;

        onConfirm({
            chartType,
            linkedTableId: currentTable.id || '',
            xAxisColumnId,
            yAxisColumnIds: orderedYAxisColumnIds,
            measurementType: firstYAxisLabel,
            title: title.trim() || undefined,
            referenceBands: parsedBands.length ? parsedBands : undefined,
            cutoffLines: parsedCutoffs.length ? parsedCutoffs : undefined,
            axis: parsedAxis,
            quadrant: parsedQuadrant,
            axisEndLabels: parsedAxisEndLabels,
            seriesColors: Object.keys(selectedSeriesColors).length
                ? selectedSeriesColors
                : undefined,
            seriesSymbols:
                symbolsSupported && Object.keys(selectedSeriesSymbols).length
                    ? selectedSeriesSymbols
                    : undefined,
            groupColumnId: selectedGroupColumnId,
            // Stored only when enabled — undefined means "single color family" (the default).
            colorByGroup: colorByGroupSupported && colorByGroup ? true : undefined,
            colorByClassification:
                pointEmphasisSupported && colorByClassification && parsedBands.length
                    ? true
                    : undefined,
            highlight: pointEmphasisSupported ? selectedHighlight : undefined,
            showDeltas: deltasSupported && showDeltas ? true : undefined,
            lineStyle,
            barStyle,
            pointLabels: pointLabelsSupported ? pointLabels : undefined,
            referenceColumnIds: selectedReferenceIds.length ? selectedReferenceIds : undefined,
            bandColor: bandColor || undefined,
            excludedXLabels: excludedXLabels.length ? excludedXLabels : undefined,
            pointColors:
                pointEmphasisSupported && Object.keys(selectedPointColors).length
                    ? selectedPointColors
                    : undefined,
            datapoints: previewDatapoints,
        });
        onOpenChange(false);
    };

    // Step 2 is reachable via the indicator when editing (jump straight to styling)
    // or once the chart is already valid; step 1 is always reachable.
    const canJumpToStyle = isEditing || canConfirm;

    const confirmButton = (
        <TooltipProvider>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span tabIndex={canConfirm ? undefined : 0}>
                        <Button onClick={handleConfirm} disabled={!canConfirm}>
                            {confirmLabel}
                        </Button>
                    </span>
                </TooltipTrigger>
                {!canConfirm && <TooltipContent>{emptyMessage}</TooltipContent>}
            </Tooltip>
        </TooltipProvider>
    );

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex h-[85vh] max-w-5xl flex-col overflow-hidden">
                <DialogHeader className="space-y-3">
                    <DialogTitle>{isEditing ? 'Editar gráfico' : 'Novo gráfico'}</DialogTitle>
                    <DialogDescription className="sr-only">
                        Configure a visualização em duas etapas: dados e tipo, depois estilo e
                        ajustes.
                    </DialogDescription>
                    <div className="flex items-center gap-2">
                        {STEPS.map((label, index) => {
                            const active = step === index;
                            const reachable = index === 0 || canJumpToStyle;
                            return (
                                <button
                                    key={label}
                                    type="button"
                                    disabled={!reachable}
                                    onClick={() => reachable && setStep(index)}
                                    className={cn(
                                        'flex flex-1 items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60',
                                        active
                                            ? 'border-blue-500 bg-blue-50/50 text-blue-700'
                                            : 'border-border text-muted-foreground hover:bg-surface',
                                    )}
                                >
                                    <span
                                        className={cn(
                                            'flex size-5 flex-shrink-0 items-center justify-center rounded-full text-xs font-semibold',
                                            active
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-muted text-muted-foreground',
                                        )}
                                    >
                                        {index + 1}
                                    </span>
                                    <span className="font-medium">{label}</span>
                                </button>
                            );
                        })}
                    </div>
                </DialogHeader>

                <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden md:flex-row">
                    <div className="overflow-y-auto pr-2 md:w-80 md:flex-shrink-0">
                        {step === 0 ? (
                            <DataAndTypeStep
                                lockTableSelection={lockTableSelection}
                                availableTables={availableTables}
                                currentTableId={currentTableId}
                                onTableChange={setCurrentTableId}
                                hasTable={hasTable}
                                columns={tableChartData.columns}
                                defaultXAxisColumnId={tableChartData.defaultXAxisColumnId}
                                defaultYAxisColumnId={tableChartData.defaultYAxisColumnId}
                                xAxisColumnId={xAxisColumnId}
                                onXAxisChange={setXAxisColumnId}
                                yAxisColumnIds={yAxisColumnIds}
                                multiSeries={typeDef.supports.multiSeries}
                                onToggleYAxis={toggleYAxisColumn}
                                groupingSupported={groupingSupported}
                                groupColumnId={groupColumnId}
                                onGroupColumnChange={setGroupColumnId}
                                activeChartType={chartType}
                                onApplyType={applyChartType}
                                onTypeHoverChange={setHoveredType}
                                xLabels={
                                    ROW_FILTER_TYPES.has(chartType) ? previewDatapoints.x_axis : []
                                }
                                excludedXLabels={excludedXLabels}
                                onToggleRow={(label) =>
                                    setExcludedXLabels((prev) =>
                                        prev.includes(label)
                                            ? prev.filter((item) => item !== label)
                                            : [...prev, label],
                                    )
                                }
                            />
                        ) : (
                            <StyleStep
                                typeDef={typeDef}
                                hasTable={hasTable}
                                title={title}
                                onTitleChange={setTitle}
                                showDecorations={showDecorations}
                                bands={referenceBands}
                                cutoffs={cutoffLines}
                                yAxisMin={yAxisMin}
                                yAxisMax={yAxisMax}
                                onAddBand={addBand}
                                onUpdateBand={updateBand}
                                onRemoveBand={removeBand}
                                onAddCutoff={addCutoff}
                                onUpdateCutoff={updateCutoff}
                                onRemoveCutoff={removeCutoff}
                                onYAxisMinChange={setYAxisMin}
                                onYAxisMaxChange={setYAxisMax}
                                quadrant={{ x: quadrantX, y: quadrantY }}
                                onQuadrantChange={(patch) => {
                                    if (patch.x !== undefined) setQuadrantX(patch.x);
                                    if (patch.y !== undefined) setQuadrantY(patch.y);
                                }}
                                axisEndLabels={axisEndLabels}
                                onAxisEndLabelsChange={(patch) =>
                                    setAxisEndLabels((prev) => ({ ...prev, ...patch }))
                                }
                                columns={tableChartData.columns}
                                selectedColumnIds={orderedYAxisColumnIds}
                                seriesColors={seriesColors}
                                primaryColor={primaryColor}
                                secondaryColor={secondaryColor}
                                onSeriesColorChange={(columnId, color) =>
                                    setSeriesColors((prev) => ({ ...prev, [columnId]: color }))
                                }
                                symbolsSupported={symbolsSupported}
                                seriesSymbols={seriesSymbols}
                                onSeriesSymbolChange={(columnId, symbol) =>
                                    setSeriesSymbols((prev) => {
                                        // Empty string = back to "auto": drop the key entirely.
                                        if (!symbol) {
                                            const next = { ...prev };
                                            delete next[columnId];
                                            return next;
                                        }
                                        return { ...prev, [columnId]: symbol };
                                    })
                                }
                                bandColor={bandColor}
                                onBandColorChange={setBandColor}
                                referenceColumnIds={selectedReferenceIds}
                                lineCurve={lineCurve}
                                fillArea={fillArea}
                                fillGradient={fillGradient}
                                onLineCurveChange={setLineCurve}
                                onFillAreaChange={setFillArea}
                                onFillGradientChange={setFillGradient}
                                barCorner={barCorner}
                                barThickness={barThickness}
                                barGradient={barGradient}
                                onBarCornerChange={setBarCorner}
                                onBarThicknessChange={setBarThickness}
                                onBarGradientChange={setBarGradient}
                                pointLabelsSupported={pointLabelsSupported}
                                pointLabels={pointLabels}
                                onPointLabelsChange={setPointLabels}
                                colorByGroupSupported={colorByGroupSupported}
                                colorByGroup={colorByGroup}
                                onColorByGroupChange={setColorByGroup}
                                pointEmphasisSupported={pointEmphasisSupported}
                                classificationAvailable={parsedBands.length > 0}
                                colorByClassification={colorByClassification}
                                onColorByClassificationChange={setColorByClassification}
                                xLabels={visibleXLabels}
                                pointColors={pointColors}
                                onPointColorChange={(label, color) =>
                                    setPointColors((prev) => {
                                        if (!color) {
                                            const next = { ...prev };
                                            delete next[label];
                                            return next;
                                        }
                                        return { ...prev, [label]: color };
                                    })
                                }
                                highlightLabel={highlightLabel}
                                highlightColor={highlightColor}
                                onHighlightLabelChange={setHighlightLabel}
                                onHighlightColorChange={setHighlightColor}
                                deltasSupported={deltasSupported}
                                showDeltas={showDeltas}
                                onShowDeltasChange={setShowDeltas}
                            />
                        )}
                    </div>

                    <div className="flex min-h-[300px] flex-1 flex-col gap-2">
                        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-white">
                            <div className="flex min-h-0 flex-1 items-center justify-center">
                                {(() => {
                                    const activeSpec = hoveredSpec ?? previewSpec;
                                    return !activeSpec || isChartSpecEmpty(activeSpec) ? (
                                        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
                                            {emptyMessage}
                                        </div>
                                    ) : (
                                        <div className="relative size-full overflow-hidden">
                                            <ChartZoomViewport>
                                                <ChartRenderer spec={activeSpec} />
                                            </ChartZoomViewport>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                        {skippedRowCount > 0 && (
                            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                                {skippedRowCount} linha(s) com valores vazios ou não numéricos serão
                                ignoradas no gráfico.
                            </div>
                        )}
                    </div>
                </div>

                <div className="flex items-center justify-between gap-3 border-t pt-4">
                    {step === 0 ? (
                        <>
                            <Button variant="outline" onClick={() => onOpenChange(false)}>
                                Cancelar
                            </Button>
                            <div className="flex items-center gap-3">
                                {/* Smart defaults make the chart valid at step 1 — allow a
                                    one-step save without opening the styling step. */}
                                <Button
                                    variant="outline"
                                    onClick={handleConfirm}
                                    disabled={!canConfirm}
                                >
                                    {confirmLabel}
                                </Button>
                                <Button onClick={() => setStep(1)} disabled={!hasTable}>
                                    Próximo
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => setStep(0)}>
                                Voltar
                            </Button>
                            {confirmButton}
                        </>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}
